import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import SignaturePad from './components/SignaturePad';
import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const input = { border:'1px solid ' + T.b200, borderRadius:8, padding:'9px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 14px', font:'inherit', fontWeight:600,
  fontSize:12.5, cursor:'pointer', background: primary ? T.rose : 'transparent',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : '1px solid ' + T.b300 });

const ST = {
  not_started: { bg:T.b100,      fg:T.inkSoft, text:'Not started' },
  progressing: { bg:T.infoBg,    fg:T.info,    text:'Progressing' },
  achieved:    { bg:T.passBg,    fg:T.pass,    text:'Achieved' },
  revised:     { bg:T.warningBg, fg:T.warning, text:'Revised' },
  discontinued:{ bg:T.b100,      fg:T.inkSoft, text:'Discontinued' },
};
const RATINGS = [
  ['achieved','Achieved'], ['progressing','Progressing'],
  ['limited_progress','Limited progress'], ['no_progress','No progress']];
const ACTIONS = [['continue','Continue'], ['modify','Modify'], ['complete','Complete']];
const MODALITIES = ['Individual Therapy','Group Therapy','Family Therapy','Cognitive Behavioral Therapy (CBT)','Dialectical Behavior Therapy (DBT)','Motivational Interviewing (MI)','EMDR','Trauma-Focused Therapy','Psychoeducation','Relapse Prevention','12-Step Facilitation','Case Management','Medication Management','Other'];

function Pill({ s }) {
  const st = ST[s] || ST.not_started;
  return <span style={{ background:st.bg, color:st.fg, borderRadius:99, padding:'2px 10px',
    fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{st.text}</span>;
}
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone:'UTC' }) : '';

function AddRow({ placeholder, extra, extra2, select, onAdd }) {
  const [text, setText] = useState('');
  const [x, setX] = useState('');
  const [y, setY] = useState('');
  const [z, setZ] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!text.trim()) return;
    setBusy(true);
    await onAdd(text.trim(), x.trim(), y.trim(), z);
    setText(''); setX(''); setY(''); setZ(''); setBusy(false);
  };
  return (
    <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
      <input style={{ ...input, flex:'1 1 220px' }} placeholder={placeholder} value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') go(); }} />
      {extra && <input style={{ ...input, flex:'0 1 150px' }} placeholder={extra} value={x}
        onChange={e => setX(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') go(); }} />}
      {extra2 && <input style={{ ...input, flex:'0 1 150px' }} placeholder={extra2} value={y}
        onChange={e => setY(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') go(); }} />}
      {select && <select style={{ ...input, flex:'0 1 190px' }} value={z} onChange={e => setZ(e.target.value)}>
        <option value="">{select.placeholder}</option>
        {select.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>}
      <button style={{ ...btn(false), flexShrink:0 }} disabled={busy || !text.trim()} onClick={go}>Add</button>
    </div>
  );
}

function ChoiceRow({ options, value, onPick }) {
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onPick(v)}
          style={{ borderRadius:99, padding:'6px 13px', fontSize:12, fontWeight:600, cursor:'pointer',
            border:'1px solid ' + (value === v ? T.rose : T.b200),
            background: value === v ? T.rose : '#fff',
            color: value === v ? '#fff' : T.inkSoft }}>{label}</button>
      ))}
    </div>
  );
}

function ReviewMode({ episodeId, goals, onDone }) {
  const [entries, setEntries] = useState(() => {
    const m = {};
    goals.filter(g => g.status === 'active').forEach(g => {
      m[g.id] = { progress_rating:'', client_perspective:'', staff_assessment:'', plan_action:'continue' };
    });
    return m;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const active = goals.filter(g => g.status === 'active');
  const setF = (gid, k, v) => setEntries(s => ({ ...s, [gid]: { ...s[gid], [k]: v } }));
  const ready = active.every(g => entries[g.id] && entries[g.id].progress_rating
    && entries[g.id].client_perspective.trim() && entries[g.id].staff_assessment.trim()
    && entries[g.id].plan_action);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const rr = await axios.post(API + '/api/emr/tp/' + episodeId + '/review', {
        entries: active.map(g => ({ goal_id: g.id, ...entries[g.id],
          client_perspective: entries[g.id].client_perspective || null,
          staff_assessment: entries[g.id].staff_assessment || null })) });
      onDone(rr.data.note_id || true);
    } catch { setErr('Could not save review'); setBusy(false); }
  };
  return (
    <div style={{ background:T.bg, borderRadius:10, padding:'14px 16px', marginTop:12 }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>Review together</div>
      <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:12 }}>
        Walk each goal with the client. The plan carries forward - nothing gets retyped.</div>
      {active.map(g => (
        <div key={g.id} style={{ background:'#fff', border:'1px solid ' + T.b200, borderRadius:10,
          padding:'12px 14px', marginBottom:10 }}>
          <div style={{ fontWeight:600, marginBottom:8 }}>{g.goal_number}. {g.goal_text}</div>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:10,
            background:T.bg, borderRadius:8, padding:'8px 12px' }}>
            <div style={{ flex:'1 1 220px' }}>
              <div style={{ fontSize:10.5, color:T.inkSoft, fontWeight:600, marginBottom:3 }}>OBJECTIVES</div>
              {(g.objectives || []).map(o => (
                <div key={o.id} style={{ fontSize:12.5, padding:'2px 0', display:'flex', gap:8,
                  justifyContent:'space-between', alignItems:'baseline' }}>
                  <span>{o.objective_text}</span><Pill s={o.status} />
                </div>
              ))}
              {!(g.objectives || []).length &&
                <div style={{ fontSize:12, color:T.inkSoft }}>No objectives on this goal</div>}
            </div>
            <div style={{ flex:'1 1 200px' }}>
              <div style={{ fontSize:10.5, color:T.inkSoft, fontWeight:600, marginBottom:3 }}>INTERVENTIONS</div>
              {(g.objectives || []).flatMap(o => o.interventions || []).map(iv => (
                <div key={iv.id} style={{ fontSize:12.5, padding:'2px 0' }}>
                  {iv.intervention_text}
                  <span style={{ color:T.inkSoft }}>
                    {[iv.modality, iv.discipline, iv.frequency].filter(Boolean).length
                      ? ' - ' + [iv.modality, iv.discipline, iv.frequency].filter(Boolean).join(' \u00b7 ') : ''}</span>
                </div>
              ))}
              {!(g.objectives || []).flatMap(o => o.interventions || []).length &&
                <div style={{ fontSize:12, color:T.inkSoft }}>No interventions on this goal</div>}
            </div>
          </div>
          <div style={{ fontSize:11.5, color:T.inkSoft, marginBottom:5 }}>Progress since last review</div>
          <ChoiceRow options={RATINGS} value={entries[g.id].progress_rating}
            onPick={v => setF(g.id, 'progress_rating', v)} />
          <div style={{ display:'flex', gap:10, marginTop:10, flexWrap:'wrap' }}>
            <div style={{ flex:'1 1 240px' }}>
              <div style={{ fontSize:11.5, color:T.inkSoft, marginBottom:4 }}>Client perspective</div>
              <textarea style={{ ...input, minHeight:56 }} placeholder="What does the client say about their progress?"
                value={entries[g.id].client_perspective}
                onChange={e => setF(g.id, 'client_perspective', e.target.value)} />
            </div>
            <div style={{ flex:'1 1 240px' }}>
              <div style={{ fontSize:11.5, color:T.inkSoft, marginBottom:4 }}>Staff assessment</div>
              <textarea style={{ ...input, minHeight:56 }} placeholder="Clinical assessment of progress"
                value={entries[g.id].staff_assessment}
                onChange={e => setF(g.id, 'staff_assessment', e.target.value)} />
            </div>
          </div>
          <div style={{ fontSize:11.5, color:T.inkSoft, margin:'10px 0 5px' }}>Plan for this goal</div>
          <ChoiceRow options={ACTIONS} value={entries[g.id].plan_action}
            onPick={v => setF(g.id, 'plan_action', v)} />
        </div>
      ))}
      {err && <div style={{ color:T.blocking, fontSize:13, marginBottom:8 }}>{err}</div>}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <button style={btn(true)} disabled={busy || !ready} onClick={save}>
          {ready ? 'Save review' : 'Every goal needs a rating, client perspective, staff assessment, and plan'}</button>
        <button style={btn(false)} disabled={busy} onClick={() => onDone(false)}>Cancel</button>
      </div>
    </div>
  );
}

function StatusForm({ objId, onDone }) {
  const [status, setStatus] = useState('progressing');
  const [comment, setComment] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(API + '/api/emr/tp/objectives/' + objId + '/status',
        { status, comment: comment || null, target_date: target || null });
      onDone(true);
    } catch { onDone(false); }
    setBusy(false);
  };
  return (
    <div style={{ background:T.bg, borderRadius:8, padding:'10px 12px', marginTop:8 }}>
      <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
        <select style={{ ...input, width:'auto' }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="not_started">Not started</option><option value="progressing">Progressing</option>
          <option value="achieved">Achieved</option><option value="revised">Revised</option>
          <option value="discontinued">Discontinued</option>
        </select>
        <input type="date" style={{ ...input, width:'auto' }} value={target}
          onChange={e => setTarget(e.target.value)} title="Next target date" />
      </div>
      <textarea style={{ ...input, minHeight:52 }} placeholder="Status comment (what happened since last review)"
        value={comment} onChange={e => setComment(e.target.value)} />
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button style={btn(true)} disabled={busy} onClick={save}>Save status</button>
        <button style={btn(false)} disabled={busy} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </div>
  );
}

function SignCeremony({ noteId, initialStaff, initialClient, onDone }) {
  const [pin, setPin] = useState('');
  const [staffDone, setStaffDone] = useState(!!initialStaff);
  const [clientImg, setClientImg] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientDone, setClientDone] = useState(!!initialClient);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const staffSign = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(API + '/api/emr/notes/' + noteId + '/sign', { method:'pin', pin });
      setStaffDone(true); setPin('');
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Staff sign failed'); }
    setBusy(false);
  };
  const clientSign = async () => {
    setErr('');
    if (!clientImg) { setErr('Client must draw their signature'); return; }
    if (!clientName.trim()) { setErr('Enter the client name'); return; }
    setBusy(true);
    try {
      await axios.post(API + '/api/emr/notes/' + noteId + '/client-signature',
        { image_base64: clientImg, client_name: clientName.trim() });
      setClientDone(true);
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Client sign failed'); }
    setBusy(false);
  };
  useEffect(() => { if (staffDone && clientDone) onDone(); }, [staffDone, clientDone, onDone]);
  return (
    <div style={{ background:T.bg, borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>Sign the review</div>
      <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:12 }}>
        Both signatures are required before this review counts for compliance.</div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <div style={{ flex:'1 1 240px', background:'#fff', border:'1px solid ' + T.b200,
          borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>
            Staff signature {staffDone && <span style={{ color:T.pass }}>- signed</span>}</div>
          {!staffDone && <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <input style={{ ...input, flex:'0 1 120px' }} type="password" inputMode="numeric"
              maxLength={4} placeholder="4-digit PIN" value={pin}
              onChange={e => setPin(e.target.value.replace(/[^0-9]/g, ''))} />
            <button style={btn(true)} disabled={busy || pin.length !== 4} onClick={staffSign}>Sign</button>
          </div>}
        </div>
        <div style={{ flex:'1 1 280px', background:'#fff', border:'1px solid ' + T.b200,
          borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>
            Client signature {clientDone && <span style={{ color:T.pass }}>- signed</span>}</div>
          {!clientDone && <div>
            <div style={{ fontSize:12, color:T.inkSoft, marginBottom:6 }}>
              Hand the device to the client to sign below.</div>
            <SignaturePad onChange={setClientImg} height={120} />
            <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
              <input style={{ ...input, flex:'1 1 160px' }} placeholder="Client full name"
                value={clientName} onChange={e => setClientName(e.target.value)} />
              <button style={btn(true)} disabled={busy} onClick={clientSign}>Save signature</button>
            </div>
          </div>}
        </div>
      </div>
      {err && <div style={{ color:T.blocking, fontSize:13, marginTop:8 }}>{err}</div>}
    </div>
  );
}

export default function TPSpine({ episodeId }) {
  const [problems, setProblems] = useState([]);
  const [goals, setGoals] = useState([]);
  const [err, setErr] = useState('');
  const [statusFor, setStatusFor] = useState(null);
  const [showLog, setShowLog] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [signNote, setSignNote] = useState(null);
  const [signedOk, setSignedOk] = useState(false);

  const load = useCallback(async () => {
    if (!episodeId) return;
    try {
      const r = await axios.get(API + '/api/emr/tp/' + episodeId);
      setProblems(r.data.problems || []);
      setGoals(r.data.goals || []);
      setSignNote(r.data.pending_review_note || null);
      setErr('');
    } catch { setErr('Could not load treatment plan'); }
  }, [episodeId]);
  useEffect(() => { load(); }, [load]);

  const addProblem = async (text) => {
    try { await axios.post(API + '/api/emr/tp/' + episodeId + '/problems', { problem_text: text }); await load(); }
    catch { setErr('Could not add problem'); }
  };
  const addGoal = async (text, target) => {
    try { await axios.post(API + '/api/emr/tp/' + episodeId + '/goals',
      { goal_text: text, target_date: target || null }); await load(); }
    catch { setErr('Could not add goal'); }
  };
  // GOAL STATUS (Catherine's ticket 2fccd7de, Chad 11/2): a goal is never deleted — it is discontinued or resolved, and the Master Treatment Plan rebuilds from the active ones.
  const setGoalStatus = async (goalId, status) => {
    if (status === 'discontinued' && !window.confirm('Discontinue this goal? It stays on the record greyed out and drops off the Master Treatment Plan.')) return;
    try { await axios.patch(API + '/api/emr/tp/goals/' + goalId, { status }); await load(); } catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Could not update the goal'); }
  };
  const linkProblem = async (goalId, problemId) => {
    try { await axios.patch(API + '/api/emr/tp/goals/' + goalId, { problem_id: problemId || null }); await load(); }
    catch { setErr('Could not link problem'); }
  };
  const addObjective = async (goalId, text, target) => {
    try { await axios.post(API + '/api/emr/tp/goals/' + goalId + '/objectives',
      { objective_text: text, target_date: target || null }); await load(); }
    catch { setErr('Could not add objective'); }
  };
  const addIntervention = async (objId, text, freq, disc, modality) => {
    try { await axios.post(API + '/api/emr/tp/objectives/' + objId + '/interventions',
      { intervention_text: text, frequency: freq || null, discipline: disc || null, modality: modality || null }); await load(); }
    catch { setErr('Could not add intervention'); }
  };

  const lastReview = goals.flatMap(g => g.review_entries || [])
    .map(r => new Date(r.created_at)).sort((a, b) => b - a)[0] || null;
  const anchor = lastReview || (goals.length
    ? goals.map(g => new Date(g.created_at)).sort((a, b) => a - b)[0] : null);
  let dueLine = null;
  if (anchor) {
    const due = new Date(anchor.getTime() + 7 * 86400000);
    const days = Math.ceil((due - Date.now()) / 86400000);
    dueLine = days < 0
      ? { text:'Review overdue by ' + Math.abs(days) + ' day' + (Math.abs(days) > 1 ? 's' : ''), color:T.blocking }
      : days <= 3
        ? { text:'Next review due in ' + days + ' day' + (days === 1 ? '' : 's'), color:T.warning }
        : { text:'Next review due ' + fmtD(due), color:T.inkSoft };
  }

  if (!episodeId) return null;
  return (
    <div style={{ background:T.surface, border:'1px solid ' + T.b200, borderRadius:10, marginBottom:16 }}>
      <div style={{ padding:'13px 18px', borderBottom:'1px solid ' + T.b100,
        display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div>
          <strong>Treatment plan</strong>
          <div style={{ fontSize:12, color:T.inkSoft, marginTop:2 }}>
            {lastReview ? 'Last reviewed ' + fmtD(lastReview) : (goals.length ? 'Not yet reviewed' : 'No plan yet')}
            {dueLine && <span style={{ color:dueLine.color, fontWeight:600 }}> {' \u00b7 '} {dueLine.text}</span>}
          </div>
        </div>
        {reviewing
          ? <button style={btn(false)} onClick={() => setReviewing(false)}>Cancel review</button>
          : goals.some(g => g.status === 'active') && <button style={btn(true)} onClick={() => setReviewing(true)}>Start review</button>}
      </div>
      <div style={{ padding:18 }}>
        {err && <div style={{ color:T.blocking, fontSize:13, marginBottom:10 }}>{err}</div>}

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11.5, color:T.inkSoft, fontWeight:600, marginBottom:6 }}>PROBLEMS / NEEDS</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {problems.map(p => (
              <span key={p.id} style={{ background:T.bg, border:'1px solid ' + T.b200, borderRadius:99,
                padding:'4px 12px', fontSize:12.5,
                opacity: p.status === 'active' ? 1 : 0.5 }}>{p.problem_text}</span>
            ))}
            {!problems.length && <span style={{ fontSize:12.5, color:T.inkSoft }}>
              None yet - pull from diagnoses and assessment, or add below.</span>}
          </div>
          <AddRow placeholder="Add a problem / need..." onAdd={(t) => addProblem(t)} />
        </div>

        {signNote &&
          <SignCeremony key={signNote.id} noteId={signNote.id}
            initialStaff={signNote.staff_signed} initialClient={signNote.client_signed}
            onDone={async () => { setSignNote(null); setSignedOk(true); await load(); }} />}
        {signedOk && !signNote &&
          <div style={{ background:T.passBg, color:T.pass, borderRadius:8,
            padding:'10px 14px', marginBottom:12, fontSize:13, fontWeight:600 }}>
            Review signed by client and staff - filed to the Clinical tab and counted for compliance.
          </div>}
        {reviewing &&
          <ReviewMode episodeId={episodeId} goals={goals}
            onDone={async (ok) => { setReviewing(false);
              if (ok) { setSignedOk(false); await load(); } }} />}

        {goals.map(g => (
          <div key={g.id} style={{ borderTop:'1px solid ' + T.b100, paddingTop:14, marginTop:14,
            opacity: g.status === 'discontinued' ? 0.55 : 1 }}>
            <div style={{ display:'flex', gap:10, alignItems:'baseline', flexWrap:'wrap' }}>
              <span style={{ fontWeight:700 }}>{g.goal_number}. {g.goal_text}</span>
              {g.status !== 'active' && <span style={{ fontSize:11.5, color:T.inkSoft, border:'1px solid ' + T.b100, borderRadius:99, padding:'1px 8px' }}>{g.status}</span>}
              <span style={{ marginLeft:'auto', display:'inline-flex', gap:6 }}>
                {g.status === 'active' && <button style={{ ...btn(false), fontSize:11.5, padding:'2px 8px' }} onClick={() => setGoalStatus(g.id, 'resolved')}>Resolved</button>}
                {g.status === 'active' && <button style={{ ...btn(false), fontSize:11.5, padding:'2px 8px' }} onClick={() => setGoalStatus(g.id, 'discontinued')}>Discontinue</button>}
                {g.status !== 'active' && <button style={{ ...btn(false), fontSize:11.5, padding:'2px 8px' }} onClick={() => setGoalStatus(g.id, 'active')}>Reactivate</button>}
              </span>
            </div>
            <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3, display:'flex', gap:8,
              alignItems:'center', flexWrap:'wrap' }}>
              <select style={{ border:'none', background:'transparent', color:T.inkSoft, font:'inherit',
                fontSize:12.5, cursor:'pointer', padding:0 }}
                value={g.problem_id || ''} onChange={e => linkProblem(g.id, e.target.value)}>
                <option value="">Link a problem...</option>
                {problems.map(p => <option key={p.id} value={p.id}>{p.problem_text}</option>)}
              </select>
              {g.target_date && <span>Target {fmtD(g.target_date)}</span>}
              {(g.review_entries || []).length > 0 && (() => {
                const last = g.review_entries[g.review_entries.length - 1];
                const lab = RATINGS.find(r => r[0] === last.progress_rating);
                return <span>Last review: {lab ? lab[1] : last.progress_rating}</span>;
              })()}
            </div>

            <div style={{ display:'flex', gap:16, marginTop:10, flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 280px' }}>
                <div style={{ fontSize:11.5, color:T.inkSoft, fontWeight:600, marginBottom:4 }}>OBJECTIVES</div>
                {(g.objectives || []).map(o => (
                  <div key={o.id} style={{ borderTop:'1px solid ' + T.b100, padding:'7px 0' }}>
                    <div style={{ display:'flex', gap:8, justifyContent:'space-between',
                      alignItems:'baseline', flexWrap:'wrap' }}>
                      <span style={{ fontSize:13.5, flex:'1 1 160px' }}>{o.objective_text}</span>
                      <span style={{ display:'flex', gap:6, alignItems:'baseline' }}>
                        {o.target_date && <span style={{ fontSize:11.5, color:T.inkSoft }}>{fmtD(o.target_date)}</span>}
                        <Pill s={o.status} />
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:12, marginTop:4, alignItems:'center', flexWrap:'wrap' }}>
                      <button style={{ background:'none', border:'none', color:T.info, fontSize:11.5,
                        cursor:'pointer', padding:0 }}
                        onClick={() => setStatusFor(statusFor === o.id ? null : o.id)}>Update status</button>
                      {(o.status_entries || []).length > 0 &&
                        <button style={{ background:'none', border:'none', color:T.inkSoft, fontSize:11.5,
                          cursor:'pointer', padding:0 }}
                          onClick={() => setShowLog(s => ({ ...s, [o.id]: !s[o.id] }))}>
                          {showLog[o.id] ? 'Hide' : 'Show'} history ({o.status_entries.length})</button>}
                    </div>
                    {statusFor === o.id &&
                      <StatusForm objId={o.id} onDone={async (ok) => { setStatusFor(null); if (ok) await load(); }} />}
                    {showLog[o.id] && (o.status_entries || []).map(se => (
                      <div key={se.id} style={{ fontSize:12, marginTop:6, padding:'7px 10px',
                        background:T.bg, borderRadius:7 }}>
                        <Pill s={se.status} /> <b>{fmtD(se.entry_date)}</b>
                        {se.entered_by_name ? ' - ' + se.entered_by_name : ''}
                        {se.comment && <div style={{ marginTop:3, color:T.ink }}>{se.comment}</div>}
                      </div>
                    ))}
                  </div>
                ))}
                <AddRow placeholder="Add objective..." extra="Target (YYYY-MM-DD)"
                  onAdd={(t, x) => addObjective(g.id, t, x)} />
              </div>

              <div style={{ flex:'1 1 280px' }}>
                <div style={{ fontSize:11.5, color:T.inkSoft, fontWeight:600, marginBottom:4 }}>INTERVENTIONS</div>
                {(g.objectives || []).flatMap(o => o.interventions || []).map(iv => (
                  <div key={iv.id} style={{ borderTop:'1px solid ' + T.b100, padding:'7px 0',
                    display:'flex', gap:8, justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap' }}>
                    <span style={{ fontSize:13.5, flex:'1 1 160px' }}>{iv.intervention_text}</span>
                    <span style={{ fontSize:11.5, color:T.inkSoft, whiteSpace:'nowrap' }}>
                      {[iv.modality, iv.discipline, iv.frequency].filter(Boolean).join(' \u00b7 ')}</span>
                  </div>
                ))}
                {(g.objectives || []).length > 0 &&
                  <AddRow placeholder="Add intervention..." extra="Discipline" extra2="Frequency" select={{ placeholder: 'Modality', options: MODALITIES }}
                    onAdd={(t, d, f, m) => addIntervention(g.objectives[0].id, t, f, d, m)} />}
              </div>
            </div>
          </div>
        ))}

        <div style={{ borderTop: goals.length ? '1px solid ' + T.b100 : 'none',
          paddingTop: goals.length ? 14 : 0, marginTop: goals.length ? 14 : 0 }}>
          <AddRow placeholder={goals.length ? 'Add another goal...' : 'Add the first goal (the patient own words work well here)...'}
            extra="Target (YYYY-MM-DD)" onAdd={(t, x) => addGoal(t, x)} />
        </div>
      </div>
    </div>
  );
}
